/** @jsxImportSource @emotion/react */
'use memo'

// web/src/features/event/components/DeleteEventModal.tsx
import css from '@emotion/css/macro'
import { Radio } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import {
  DELETE_SCOPE_OPTIONS,
  type EventSeriesScope,
} from '@sportninja/common/features/event/seriesScope'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { paragraphSmall, weightRegular } from 'src/components/css'
import WarningModal from 'src/components/WarningModal'

interface DeleteEventModalProps {
  isOpen: boolean
  eventName: string
  isLoading: boolean
  /** True when the event repeats — shows the scope choices instead of a
   *  plain confirm. */
  isSeries?: boolean
  /** Selected scope. Only meaningful when `isSeries`. */
  scope: EventSeriesScope
  onScopeChange: (scope: EventSeriesScope) => void
  onClose: () => void
  onConfirm: () => void
}

const optionsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;

  .ant-radio-wrapper {
    ${paragraphSmall}
    ${weightRegular}
    color: ${colors.WHITE};
    align-items: center;
    margin-inline-end: 0;
    white-space: normal;
  }
`

/**
 * Confirmation before permanently deleting an event.
 *
 * A one-off event gets a plain confirm. A recurring one additionally picks
 * which occurrences to remove — the choice rides on the same modal rather
 * than a separate menu, so the destructive action and its scope are confirmed
 * together rather than in two steps.
 */
const DeleteEventModal: React.FC<DeleteEventModalProps> = ({
  isOpen,
  eventName,
  isLoading,
  isSeries = false,
  scope,
  onScopeChange,
  onClose,
  onConfirm,
}) => (
  <WarningModal
    isOpen={isOpen}
    onClose={onClose}
    onConfirm={onConfirm}
    title={capitalizeWords('Delete Event')}
    description={
      isSeries ? (
        <>
          {`"${eventName}" is a recursive event. Choose which events to delete. This cannot be undone.`}
          <div css={optionsStyle}>
            <Radio.Group
              value={scope}
              onChange={(e) => onScopeChange(e.target.value)}
              css={css`
                display: flex;
                flex-direction: column;
                gap: 12px;
              `}
            >
              {DELETE_SCOPE_OPTIONS.map((option) => (
                <Radio key={option.scope} value={option.scope}>
                  {capitalizeWords(option.label)}
                </Radio>
              ))}
            </Radio.Group>
          </div>
        </>
      ) : (
        `Are you sure you want to delete "${eventName}"? This cannot be undone.`
      )
    }
    cancelLabel='CANCEL'
    confirmLabel='DELETE'
    loadingLabel='DELETING...'
    isLoading={isLoading}
    destructive
  />
)

export default DeleteEventModal
